<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { loadCompanyPage } from '../company-content';
import type { ContentPage } from '../api';
import { locale, text } from '../i18n';
import { applyDocumentSeo } from '../seo';

defineOptions({ name: 'QualityView' });

type Certificate = { src: string; alt: string; caption: string };

// 仅针对已核对的更名前原件显示说明；后台替换这些图片后说明自动消失。
const legacyCertificateFiles = [
  '5e96fe03f81559bad52accb70ac5e0c51a2ce6112d6e4e8bed720317c026895a.png',
  '13b5e003b520710a3f09ef2055af573a9854963a4a3d4b486becd225ded9c067.jpg',
  'fd0b11716ea3d91fa98910746885e6a8b57177c65af9af2f8d952620de9ed549.jpg',
  '0f28dddac0a48e7bc5ced31ff6af2e15669c44537c0678fbff37b7289ccf71d2.jpg'
];

const page = ref<ContentPage | null>(null);
const error = ref('');

const certificateImages = computed<Certificate[]>(() => {
  if (!page.value?.contentHtml) return [];
  const document = new DOMParser().parseFromString(page.value.contentHtml, 'text/html');
  return [...document.images].map((image, index) => ({
    src: image.src,
    alt: image.alt || page.value?.title || '',
    caption: image.closest('figure')?.querySelector('figcaption')?.textContent?.trim()
      || text(`质量文件 ${index + 1}`, `Quality document ${index + 1}`)
  }));
});
const hasLegacyCertificates = computed(() => certificateImages.value.some((image) =>
  legacyCertificateFiles.some((filename) => new URL(image.src, window.location.href).pathname.endsWith(`/${filename}`))));

const bodyHtml = computed(() => {
  if (!page.value?.contentHtml) return '';
  const document = new DOMParser().parseFromString(page.value.contentHtml, 'text/html');
  document.querySelectorAll('img').forEach((image) => {
    const figure = image.closest('figure');
    if (figure) {
      figure.remove();
      return;
    }
    image.remove();
  });
  document.querySelectorAll('p, figure').forEach((element) => {
    if (!element.textContent?.trim()) element.remove();
  });
  return document.body.innerHTML;
});

watch(locale, async () => {
  error.value = '';
  try {
    const nextPage = await loadCompanyPage(locale.value, 'quality');
    page.value = nextPage;
    applyDocumentSeo(nextPage.seo.title, nextPage.seo.description);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : text('页面加载失败', 'Unable to load page');
  }
}, { immediate: true });
</script>

<template>
  <p v-if="error" class="container portal-page-error">{{ error }}</p>
  <template v-else-if="page">
    <section class="portal-section">
      <div class="container">
        <header class="portal-section-heading">
          <span>{{ text('质量管理', 'Quality management') }}</span>
          <h2>{{ text('用可追溯的过程保证交付质量', 'Assure delivery quality through traceable processes') }}</h2>
        </header>
        <p v-if="hasLegacyCertificates" class="portal-quality-document-note">
          {{ text('资料说明：以下部分证书及检测报告为更名前出具的原始文件，所载公司名称和地址保留原件内容。如需更名后资料，请联系业务团队确认。', 'Document note: Some certificates and test reports below were issued before the company name change. Original company names and addresses are retained. Contact our team to confirm documentation for the renamed company.') }}
        </p>
        <div v-if="certificateImages.length" class="portal-certificate-grid">
          <figure v-for="certificate in certificateImages" :key="certificate.src">
            <img :src="certificate.src" :alt="certificate.alt" loading="lazy">
            <figcaption>{{ certificate.caption }}</figcaption>
          </figure>
        </div>
        <article v-if="bodyHtml" class="rich-content portal-quality-copy" v-html="bodyHtml" />
      </div>
    </section>
  </template>
</template>
